import React from "react";
import { Controller } from "react-hook-form";
import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import SelectableChoiceCard from "components/common/SelectableChoiceCard";
import {
  AUTHORITY_LEVELS,
  NEGATION_LEVELS,
} from "services/llm/promptLintService";

/**
 * Settings for the optional DSPy route (functions/src/llm/dspyRoute).
 *
 * Two audiences see this panel and they need different things from it. A global
 * admin is choosing a platform default and deciding whether organizations may
 * depart from it. An org admin is deciding whether their own organization takes
 * the route, and needs to see what the platform picked before they override it.
 */

const LevelPicker = ({ name, control, options, disabled }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Grid container spacing={1.5}>
        {options.map((option) => (
          <Grid key={option.value} item xs={12} sm={6}>
            <SelectableChoiceCard
              title={option.label}
              description={option.description}
              selected={field.value === option.value}
              disabled={disabled}
              onClick={() => field.onChange(option.value)}
            />
          </Grid>
        ))}
      </Grid>
    )}
  />
);

const DspyRoutePanel = ({
  control,
  routeEnabled,
  negationEnabled,
  authorityEnabled,
  canEditGlobal = false,
  lockedByGlobal = false,
  globalRoute = null,
}) => {
  const readOnly = !canEditGlobal && lockedByGlobal;

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="subtitle2">Enhanced AI mode</Typography>
        <Typography variant="body2" color="text.secondary">
          In this setting, the AI will process the responses more thoroughly.
        </Typography>
      </Box>

      {readOnly ? (
        <Alert severity="info">
          A platform administrator locked this setting for every organization on
          this prompt.
        </Alert>
      ) : null}

      {!canEditGlobal && globalRoute ? (
        <Alert severity="info">
          Platform default for this prompt:{" "}
          {globalRoute.enabled ? "on" : "off"}. Your choice below applies to your
          organization only.
        </Alert>
      ) : null}

      <Controller
        name="dspyRoute.enabled"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(field.value)}
                disabled={readOnly}
                onChange={(event) => field.onChange(event.target.checked)}
              />
            }
            label="Enable enhanced AI mode"
          />
        )}
      />

      <Collapse in={Boolean(routeEnabled)} unmountOnExit>
        <Box sx={{ display: "grid", gap: 2 }}>
          {canEditGlobal ? (
            <Controller
              name="dspyRoute.locked"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="Lock — organizations cannot turn this off"
                />
              )}
            />
          ) : null}

          <Divider />

          <Box>
            <Controller
              name="dspyRoute.negation.enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value !== false}
                      disabled={readOnly}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="Negation directives"
                />
              )}
            />
            <Collapse in={negationEnabled !== false} unmountOnExit>
              <Box sx={{ mt: 1 }}>
                <LevelPicker
                  name="dspyRoute.negation.level"
                  control={control}
                  options={NEGATION_LEVELS}
                  disabled={readOnly}
                />
              </Box>
            </Collapse>
          </Box>

          <Divider />

          <Box>
            <Controller
              name="dspyRoute.authority.enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value !== false}
                      disabled={readOnly}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="Organization rule precedence"
                />
              )}
            />
            <Collapse in={authorityEnabled !== false} unmountOnExit>
              <Box sx={{ mt: 1 }}>
                <LevelPicker
                  name="dspyRoute.authority.level"
                  control={control}
                  options={AUTHORITY_LEVELS}
                  disabled={readOnly}
                />
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default DspyRoutePanel;
